import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { baseUrl } from 'App'
import { useGlobalContext } from 'GlobalContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons'
import { saveData } from 'services/api/saveData'
import { CustomFieldPhotoValueType } from './CustomFieldTypes'
import { useModal } from 'components/modals/ModalContext'
import PhotoViewModal, { PhotoViewType } from 'components/modals/PhotoViewModal'

interface CustomFieldPhotoValueProps {
    photos: CustomFieldPhotoValueType[]
    editable?: boolean
    objectType?: string
    itemId?: string | number
    fieldId: number
    onChange?: (photos: CustomFieldPhotoValueType[] | ((currentPhotos: CustomFieldPhotoValueType[]) => CustomFieldPhotoValueType[])) => void
}

const CustomFieldPhoto: React.FC<CustomFieldPhotoValueProps> = ({
    photos,
    editable = false,
    objectType,
    itemId,
    fieldId,
    onChange,
}) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { setFloatingAlert } = useGlobalContext();
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Get the file url
    const getFileUrl = (file?: string | null) => {
        let fileUrl = ''

        if (file) {
            fileUrl = process.env.NODE_ENV === 'production' ? file : `${baseUrl}${file}`
        }

        return fileUrl
    }

    // Open the file selector
    const openFileSelector = (event: React.MouseEvent) => {
        event.stopPropagation()

        if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }

    // Upload the photo to the backend
    const uploadPhoto = async (fileUpload: string, filename: string) => {
        if (objectType && itemId !== undefined && itemId !== null) {
            setIsUploading(true)

            try {
                const response: any = await saveData({
                    apiUrl: `post_custom_field_photo/${objectType}/${itemId}`,
                    method: 'post',
                    data: {
                        field_id: fieldId,
                        custom_field_entry_id: fieldId,
                        file_upload: fileUpload,
                        filename: filename,
                    },
                })

                // Get the saved photo response
                const savedPhoto = response?.data ?? response

                if (onChange && savedPhoto?.id) {
                    onChange((currentPhotos) => {
                        const existingPhotos = Array.isArray(currentPhotos) ? currentPhotos : []
                        return [ ...existingPhotos, savedPhoto ]
                    })
                }
            } catch (error) {
                console.error(error)
            } finally {
                setIsUploading(false)
            }
        }
    }

    // Handle file change
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0] : null

        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader()

            reader.onload = async (readerEvent: ProgressEvent<FileReader>) => {
                const result = readerEvent.target?.result

                if (typeof result === 'string') {
                    await uploadPhoto(result, file.name)
                }
            }

            reader.readAsDataURL(file)
        } else {
            setFloatingAlert({
                type: 'warning',
                message: 'validation.image.unsupported_file_type',
            })
        }

        event.target.value = ''
    }

    // Delete the photo
    const deletePhoto = async (photoId: number, event: React.MouseEvent) => {
        event.stopPropagation()

        setIsUploading(true)

        try {
            await saveData({
                apiUrl: `patch_custom_field_photo/${photoId}`,
                method: 'patch',
                data: {
                    deleted: true,
                },
            })

            if (onChange) {
                onChange((currentPhotos) => {
                    const existingPhotos = Array.isArray(currentPhotos) ? currentPhotos : []
                    return existingPhotos.filter(photo => photo.id !== photoId)
                })
            }
        } catch (error) {
            console.error(error)
        } finally {
            setIsUploading(false)
        }
    };

    // Open the photo view modal
    const openPhotoViewer = (photo: CustomFieldPhotoValueType, event: React.MouseEvent) => {
        event.stopPropagation()

        initializeModal(
            <PhotoViewModal
                photo={photo as PhotoViewType}
                editable={editable}
                onSaved={(updatedPhoto) => {
                    if (onChange) {
                        onChange((currentPhotos) => {
                            const existingPhotos = Array.isArray(currentPhotos) ? currentPhotos : []
                            return existingPhotos.map(currentPhoto =>
                                currentPhoto.id === updatedPhoto.id
                                    ? { ...currentPhoto, ...updatedPhoto } as CustomFieldPhotoValueType
                                    : currentPhoto
                            )
                        })
                    }
                }}
                onDeleted={(photoId) => {
                    if (onChange) {
                        onChange((currentPhotos) => {
                            const existingPhotos = Array.isArray(currentPhotos) ? currentPhotos : []
                            return existingPhotos.filter(currentPhoto => currentPhoto.id !== photoId)
                        })
                    }
                }}
            />, {
                modalSize: 'medium',
                modalPosition: 'center',
                modalSpacing: 'compact'
            }
        )
    }

    return (
        <div className={`custom-field-photo-value ${editable ? 'editable' : ''}`}>
            <div className='custom-field-photo-grid'>
                {photos.map(photo => {
                    const imageUrl = getFileUrl(photo.thumbnail || photo.file)

                    return (
                        <div
                            key={photo.id}
                            className='custom-field-photo-item'
                            style={{ backgroundImage: imageUrl ? `url(${imageUrl})` : undefined }}
                            onClick={(event) => openPhotoViewer(photo, event)}
                        >
                            {editable && (
                                <button
                                    type='button'
                                    className='custom-field-photo-remove'
                                    onClick={(event) => deletePhoto(photo.id, event)}
                                    disabled={isUploading}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    )
                })}

                {editable && (
                    <button
                        type='button'
                        className='custom-field-photo-add'
                        onClick={openFileSelector}
                        disabled={isUploading}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        <span>
                            {isUploading
                                ? t('general.loading')
                                : t('forms.add_photo_label', 'Foto toevoegen')
                            }
                        </span>
                    </button>
                )}
            </div>

            {!editable && photos.length === 0 && (
                <span>-</span>
            )}

            <input
                type='file'
                accept='image/png, image/jpeg, image/jpg'
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />
        </div>
    )
}

export default CustomFieldPhoto;